/**
 * ArQrModal — Desktop AR fallback showing a QR code for mobile handoff.
 *
 * Displayed when the user clicks the AR button on a desktop device.
 * Instead of attempting WebXR (which is unavailable on most desktops),
 * it shows a QR code encoding the current URL with `?ar=auto`. When
 * scanned on a mobile device the app auto-launches AR after loading.
 *
 * Visual style matches ArModal: blurred backdrop, centered content,
 * AR icon, localized text, and brand-consistent button style.
 *
 * @see ArModal — mobile AR overlay (Quick Look / WebXR)
 * @see generateArQrUrl — URL builder for the QR code
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { ResolvedConfig } from '@ims/shared';
import { generateArQrUrl } from '../../utils/generateArQrUrl';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const FADE_MS = 380;
const ICON_AR = '/app/icons/icon-ar.svg';
const ICON_CLOSE = '/app/icons/icon-close.svg';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ArQrModalProps {
  open: boolean;
  onClose: () => void;
  config: ResolvedConfig | null;
}

export function ArQrModal({ open, onClose, config }: ArQrModalProps) {
  const [mounted, setMounted] = useState(false);
  const [animIn, setAnimIn] = useState(false);
  const rafRef = useRef<number | null>(null);

  const qrUrl = useMemo(() => (open ? generateArQrUrl() : ''), [open]);

  // ── mount / unmount animation (same timing as ArModal) ────────────────
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: coordinated mount/animate sequence
      setMounted(true);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setAnimIn(true));
      });
    } else {
      setAnimIn(false);
      const t = setTimeout(() => setMounted(false), FADE_MS);
      return () => clearTimeout(t);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  // ── UI text from config ───────────────────────────────────────────────
  const title = config?.ui.arQrTitle ?? 'In AR ansehen';
  const text =
    config?.ui.arQrText ??
    'Scannen Sie den QR-Code mit Ihrem Smartphone, um das Fahrzeug per Augmented Reality in Ihrer Umgebung zu platzieren.';
  const hint =
    config?.ui.arQrScanHint ?? 'Die App öffnet sich und startet den AR-Modus automatisch.';

  if (!mounted) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* ── Backdrop ───────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: animIn ? 'blur(12px)' : 'blur(0px)',
          WebkitBackdropFilter: animIn ? 'blur(12px)' : 'blur(0px)',
          background: animIn ? 'rgba(0,0,0,0.58)' : 'rgba(0,0,0,0)',
          transition: [
            `backdrop-filter ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            `-webkit-backdrop-filter ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            `background ${FADE_MS}ms ease`,
          ].join(', '),
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* ── Close button ───────────────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 17.5,
          right: 20,
          zIndex: 1,
          opacity: animIn ? 1 : 0,
          transform: animIn ? 'translateY(0px)' : 'translateY(-6px)',
          transition: animIn
            ? 'opacity 280ms ease 180ms, transform 320ms ease 160ms'
            : 'opacity 160ms ease, transform 160ms ease',
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            padding: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.8,
          }}
        >
          <img src={ICON_CLOSE} alt="" style={{ width: 20, height: 20 }} aria-hidden="true" />
        </button>
      </div>

      {/* ── Content — two-column: text left, QR right ─────────────────── */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 72,
          padding: '40px 48px',
          opacity: animIn ? 1 : 0,
          transform: animIn ? 'scale(1) translateY(0px)' : 'scale(0.96) translateY(16px)',
          transition: animIn
            ? 'opacity 320ms ease 120ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) 100ms'
            : 'opacity 200ms ease, transform 200ms ease',
        }}
      >
        {/* Left column — icon + text */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 340 }}>
          <img
            src={ICON_AR}
            alt=""
            style={{ width: 48, height: 48, opacity: 0.9 }}
            aria-hidden="true"
          />

          <h2
            style={{
              color: 'rgba(255,255,255,0.95)',
              fontSize: 22,
              fontWeight: 600,
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {title}
          </h2>

          <p
            style={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: 14,
              fontWeight: 300,
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            {text}
          </p>

          <p
            style={{
              color: 'rgba(255,255,255,0.40)',
              fontSize: 12,
              fontWeight: 300,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {hint}
          </p>
        </div>

        {/* Right column — QR code */}
        <div
          style={{
            flexShrink: 0,
            padding: 15,
            border: '2px solid rgba(255,255,255,0.9)',
            borderRadius: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <QRCodeSVG value={qrUrl} size={220} fgColor="#ffffff" bgColor="transparent" level="M" />
        </div>
      </div>
    </div>
  );
}
